import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Shield, Settings2, Users, BarChart3, Globe, Scan } from 'lucide-react';
import { DataTable, type SortConfig } from '../../components/admin/tables';
import { api } from '../../services/api';
import { UserPermissions } from '../../components/admin/UserPermissions';
import { usePermission, PERMISSIONS } from '../../hooks/usePermission';
import UserAnalytics from '../../components/admin/users/UserAnalytics';

interface UserRow {
  id: string;
  email: string;
  is_admin: boolean;
  role?: string;
  tier: string;
  created_at: string;
  last_login: string | null;
  auth_provider?: string;
  country?: string;
  total_scans?: number;
  verified_scans?: number;
}

type TabKey = 'users' | 'analytics';

const TABS: { key: TabKey; label: string; icon: typeof Users }[] = [
  { key: 'users', label: 'Users', icon: Users },
  { key: 'analytics', label: 'Analytics', icon: BarChart3 },
];

// Auth provider icons/labels
const AUTH_PROVIDERS: Record<string, { label: string; color: string }> = {
  email: { label: 'Email', color: 'bg-gray-100 text-gray-700' },
  google: { label: 'Google', color: 'bg-red-50 text-red-700' },
  facebook: { label: 'Facebook', color: 'bg-blue-50 text-blue-700' },
  apple: { label: 'Apple', color: 'bg-gray-800 text-white' },
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('users');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('');
  const [selectedUser, setSelectedUser] = useState<{ id: string; email: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'created_at', direction: 'desc' });

  // Check if current user can manage roles
  const canManageRoles = usePermission(PERMISSIONS.USERS_ROLES);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, search, tier, sortConfig.key, sortConfig.direction],
    queryFn: () => api.getAdminUsers({
      page,
      limit: 20,
      search,
      tier,
      sort: sortConfig.key,
      order: sortConfig.direction,
    }),
    enabled: activeTab === 'users',
  });

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1); // Reset to first page when sorting
  };

  // Role display helper
  const getRoleBadge = (role?: string) => {
    if (!role || role === 'user') return null;
    const roleColors: Record<string, string> = {
      super_admin: 'bg-red-100 text-red-700',
      admin: 'bg-primary-100 text-primary-700',
      moderator: 'bg-purple-100 text-purple-700',
      support: 'bg-blue-100 text-blue-700',
      content_manager: 'bg-green-100 text-green-700',
    };
    const roleLabels: Record<string, string> = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      moderator: 'Moderator',
      support: 'Support',
      content_manager: 'Content',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${roleColors[role] || 'bg-gray-100 text-gray-700'}`}>
        <Shield size={12} />
        {roleLabels[role] || role}
      </span>
    );
  };

  const handleRowClick = (user: UserRow) => {
    navigate(`/admin/users/${user.id}`);
  };

  const columns = [
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      render: (user: UserRow) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{user.email}</span>
          {getRoleBadge(user.role)}
        </div>
      ),
    },
    {
      key: 'auth_provider',
      header: 'Provider',
      sortable: true,
      render: (user: UserRow) => {
        const provider = AUTH_PROVIDERS[user.auth_provider || 'email'];
        return (
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${provider?.color || 'bg-gray-100 text-gray-700'}`}>
            {provider?.label || user.auth_provider || 'Email'}
          </span>
        );
      },
    },
    {
      key: 'country',
      header: 'Country',
      sortable: true,
      render: (user: UserRow) => (
        <span className="text-gray-600 flex items-center gap-1">
          {user.country ? (
            <>
              <Globe size={14} className="text-gray-400" />
              {user.country}
            </>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (user: UserRow) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          user.tier === 'free' ? 'bg-gray-100 text-gray-700' :
          user.tier === 'hunter' ? 'bg-blue-100 text-blue-700' :
          user.tier === 'pro' ? 'bg-green-100 text-green-700' :
          user.tier === 'seed' ? 'bg-purple-100 text-purple-700' :
          'bg-amber-100 text-amber-700'
        }`}>
          {user.tier.charAt(0).toUpperCase() + user.tier.slice(1)}
        </span>
      ),
    },
    {
      key: 'total_scans',
      header: 'Scans',
      // Note: Scans data comes from user_scan_stats table, not sortable server-side
      render: (user: UserRow) => (
        <span className="text-gray-600 flex items-center gap-1">
          <Scan size={14} className="text-gray-400" />
          {user.total_scans || 0}
          {user.verified_scans ? (
            <span className="text-green-600 text-xs">({user.verified_scans} verified)</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Joined',
      sortable: true,
      render: (user: UserRow) => (
        <span className="text-gray-500">
          {new Date(user.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'last_login',
      header: 'Last Active',
      sortable: true,
      render: (user: UserRow) => (
        <span className="text-gray-500">
          {user.last_login
            ? new Date(user.last_login).toLocaleDateString()
            : 'Never'}
        </span>
      ),
    },
    // Only show permissions column if user can manage roles
    ...(canManageRoles
      ? [
          {
            key: 'actions',
            header: '',
            render: (user: UserRow) => (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedUser({ id: user.id, email: user.email });
                }}
                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Manage permissions"
              >
                <Settings2 size={16} />
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary-600 text-primary-600 bg-primary-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'users' && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <select
              value={tier}
              onChange={(e) => {
                setTier(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Tiers</option>
              <option value="free">Free</option>
              <option value="hunter">Hunter</option>
              <option value="pro">Pro</option>
              <option value="dealer">Dealer</option>
            </select>
          </div>

          {/* Stats */}
          {data && (
            <p className="text-sm text-gray-500">
              Showing {data.users.length} of {data.total} users
            </p>
          )}

          {/* Table */}
          <DataTable
            columns={columns}
            data={data?.users || []}
            loading={isLoading}
            page={page}
            totalPages={data?.totalPages || 1}
            onPageChange={setPage}
            onRowClick={handleRowClick}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </>
      )}

      {activeTab === 'analytics' && <UserAnalytics />}

      {/* User Permissions Modal */}
      {selectedUser && (
        <UserPermissions
          userId={selectedUser.id}
          userEmail={selectedUser.email}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}
